/* eslint-disable max-lines */
import CardWrapper from '../wrapper/card';
import { createDealThunk } from '../../redux/thunks/deal';
import CustomInput from '../reusable/input';
import { DealParams } from '../../params/pipeline';
import { dealSchema } from '../../schema/deal_schema';
import { getSinglePipelineDetailThunk } from '../../redux/thunks/pipeline';
import SelectPipeline from '../pipeline/select_pipeline';
import SelectStage from '../stage/select_stage';
import { setAddingDeal } from '../../redux/slices/deal';
import { settingSelectedStage } from '../../redux/slices/stage';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Grid, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: Add Deal Component
 * @paragraph: This component is used to add a new deal.
* @interface: CustomFieldsSectionProps
 * @property: {DealParams} values - Current form values.
 * @property: {function} handleChange - Formik function to handle value changes.
 * @property: {function} handleBlur - Formik function to handle blur events.
 * @property: {object} errors - Form validation errors.
 * @property: {object} touched - Form touched fields state.
*/
interface CustomFieldsSectionProps {
    values: DealParams;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    errors: { [key: string]: string | undefined };
    touched: { [key: string]: boolean | undefined };
}

/**
 * @heading1: CustomFieldsSection
* @paragraph: A section to handle custom fields for a deal.
 * @param: {CustomFieldsSectionProps} values handleChange handleBlur errors touched - Values from interface.
 */
const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({ values, handleChange, handleBlur, errors, touched }) => (
    <Grid item xs={12}>
        <Typography variant="subtitle1">Custom Fields</Typography>
        <Grid container spacing={2} mt={1}>
            {Array.from({ length: 5 }, (_, index) => {
                const fieldName = `custom_field${index + 1}` as keyof DealParams;
                return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <CustomInput
                            fullWidth
                            label={`Custom Field ${index + 1}`}
                            name={fieldName}
                            value={values[fieldName] as string}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched[fieldName] && Boolean(errors[fieldName])}
                        />
                    </Grid>
                );
            })}
        </Grid>
    </Grid>
);

/**
* @interface: DueDateInputProps
 * @param: {string} val - Current date value.
 * @param: {(field: string, value: string) => void} setFieldValue - Formik function to update the due date field.
 * @param: {string | undefined} error - Validation error for the due date field.
 * @param: {boolean | undefined} touched - Indicates if the field has been touched.
*/
interface DueDateInputProps {
    val: string;
    setFieldValue: (field: string, value: string) => void;
    error: string | undefined;
    touched: boolean | undefined;
    disablePastDates?: boolean;
}
/**
 * @heading1: DueDateInput
* @paragraph: A date picker input component for selecting the deal's due date.
 * @param: {DueDateInputProps} val setFieldValue error touched - Values from interface.
 */
const DueDateInput: React.FC<DueDateInputProps> = ({ val, setFieldValue, error, touched, disablePastDates }) => {
    /**
     * @heading3: Variables
     * @ol:
     * isError: {boolean} - React state to check that error is true or false.
     * @separator:
     */
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const minDate = `${yyyy}-${mm}-${dd}`;
    const [isError, setIsError] = useState<boolean>(false);

    return (
        <Grid item xs={12}>
            <CustomInput
                type="date"
                label="Due Date"
                placeholder="DD-MM-YYYY"
                value={val}
                error={Boolean(error && touched)}
                showError={isError}
                errorText="Deal could not exist in the past."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const { value } = e.target;
                    const todayDate = new Date();
                    todayDate.setHours(0, 0, 0, 0);
                    const selectedDate = new Date(value);

                    if (selectedDate >= todayDate) {
                        setIsError(false);
                    } else {
                        setIsError(true);
                    }

                    setFieldValue('due_date', value);
                }}
                fullWidth
                {...(disablePastDates ? { min: minDate } : {})}
            />
        </Grid>
    );
};
/**
 * @heading1: AddDealForm
 * A form component to add a new deal. Handles pipeline and stage selection,
 * custom fields, and deal details.
 */
const AddDealForm: React.FC = () => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * selectedPipeline/pipelines/selectedStage: An object containing the details from the Redux store.
     * initialValues: {DealParams} - Initial Values for adding deal.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();
    const { selectedPipeline, pipelines } = useSelector((state: RootState) => state.pipeline);
    const { selectedStage } = useSelector((state: RootState) => state.stage);
    const { selectedClient } = useSelector((state: RootState) => state.client);
    const { loaders } = useSelector((state: RootState) => state.deal);
    const initialValues: DealParams = {
        title: '',
        email: '',
        company: id ? selectedClient?.company_name ?? '' : '',
        phone: '',
        pipeline_id: selectedPipeline ? selectedPipeline.id.toString() : '0',
        stage_id: selectedStage ? selectedStage.id.toString() : '0',
        due_date: '',
        disable_automation: 0,
        custom_field1: '',
        custom_field2: '',
        custom_field3: '',
        custom_field4: '',
        custom_field5: ''
    };

    /**
     * @heading3: Functions
     * @heading4: handleSubmit
     * @paragraph: Handles form submission and dispatches the `createDealThunk` action.
     * @param: {DealParams} values - The form values for creating a deal.
     */
    const handleSubmit = async (values: DealParams) => {
        try {
            await dispatch(createDealThunk(values)).unwrap();
        } catch (e) {
            // Catch errors
        }
    };
    const addDealHandler = () => {
        dispatch(setAddingDeal(false));
        dispatch(settingSelectedStage(null));
    };
    return (
        <Box display="flex" flexDirection="column" gap="20px">
            <CardWrapper>
                <Typography variant="h6" fontWeight="600" mb={2}>
                    Add a Deal
                </Typography>
                <Formik<DealParams> initialValues={initialValues} validationSchema={dealSchema} onSubmit={handleSubmit}>
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
                        return <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    {/* Input field for deal title */}
                                    <CustomInput
                                        fullWidth
                                        label="Title"
                                        name="title"
                                        value={values.title}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.title && Boolean(errors.title)}
                                        mandatoryFields={true}
                                        disabled={loaders.isCreating}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {/* Input field for email */}
                                    <CustomInput
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && Boolean(errors.email)}
                                        mandatoryFields={true}
                                        disabled={loaders.isCreating}
                                    />
                                </Grid>
                                {!id && <Grid item xs={12} sm={6}>
                                    {/* Input field for company */}
                                    <CustomInput
                                        fullWidth
                                        label="Company"
                                        name="company"
                                        value={values.company}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.company && Boolean(errors.company)}
                                        mandatoryFields={true}
                                        disabled={loaders.isCreating}
                                    />
                                </Grid>}
                                <Grid item xs={12} sm={6}>
                                    {/* Input field for phone */}
                                    <CustomInput
                                        type="phone"
                                        value={values.phone}
                                        onChangeForPhone={handleChange('phone')}
                                        onBlur={handleBlur('phone')}
                                        error={touched.phone && errors.phone}
                                        mandatoryFields={true}
                                        fullWidth
                                        label="Phone"
                                        placeholder="201-555-0123"
                                        variant="outlined"
                                        name="deal_phone"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {/* Dropdown field for pipeline */}
                                    <SelectPipeline
                                        label="Pipeline"
                                        isMandatory={true}
                                        pipelines={pipelines}
                                        onChange={async (e) => {
                                            try {
                                                await dispatch(getSinglePipelineDetailThunk(e.target.value as number)).unwrap();
                                            } catch (err: unknown) {
                                                // Catch errors
                                            }
                                            setFieldValue('pipeline_id', String(e.target.value));
                                        }}
                                        error={Boolean(errors.pipeline_id && touched.pipeline_id)}
                                        value={values.pipeline_id}
                                        disabled={loaders.isCreating}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {/* Dropdown field for stage */}
                                    <SelectStage
                                        label="Stage"
                                        isMandatory={true}
                                        stages={selectedPipeline?.pipeline_stages || []}
                                        onChange={(e) => setFieldValue('stage_id', String(e.target.value))}
                                        error={Boolean(errors.stage_id && touched.stage_id)}
                                        value={values.stage_id}
                                    // disabled={loaders.isCreating}
                                    />
                                </Grid>
                                {/* Input field for due date */}
                                <Grid item xs={12} sm={4}>
                                    <DueDateInput
                                        val={values.due_date}
                                        setFieldValue={setFieldValue}
                                        error={errors.due_date}
                                        touched={touched.due_date}
                                        disablePastDates={true}
                                    />
                                </Grid>
                                {/* Custom Input fields */}
                                <CustomFieldsSection
                                    values={values}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    errors={errors}
                                    touched={touched}
                                />
                                {/* Checkbox for Automation */}
                                {/* <Grid item xs={12}>
                                    <CustomInput
                                        type="checkbox"
                                        label="Disable Automation"
                                        checked={values.disable_automation === 1}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('disable_automation', e.target.checked ? 1 : 0)}
                                    />
                                </Grid> */}
                            </Grid>
                            {/* Action Buttons */}
                            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                                <Button variant="outlined" color="primary" onClick={addDealHandler} disabled={loaders.isCreating}>
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="primary" disabled={loaders.isCreating}>
                                    {loaders.isCreating ? 'Adding...' : 'Add'}
                                </Button>
                            </Box>
                        </Form>;
                    }}
                </Formik>
            </CardWrapper>
        </Box>
    );
};
export default AddDealForm;